import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Collapse, CardBody, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import BoolInput from '../../../../../misc/BoolInput';
import HaldorClassSetting from './HaldorClassSetting';
import HaldorEducationGroupSetting from './HaldorEducationGroupSetting';
import HaldorMentorGroupSetting from './HaldorMentorGroupSetting';
import { CoreHaldorExportConfiguration } from '../../../../../../models/CoreHaldorModels';


export const validator = (configuration: CoreHaldorExportConfiguration, active: boolean) => {
    const validation: string[] = [];

    if (active) {
        if (!configuration.blobContainerUrl || configuration.blobContainerUrl === "") {
            validation.push('blobContainerUrl');
        }
        if (!configuration.blobSourceKey || configuration.blobSourceKey === "") {
            validation.push('blobSourceKey');
        }
    }
    return validation;
}

interface HaldorConfigurationProps {
    configuration: CoreHaldorExportConfiguration
    disabled: boolean
    updatePart: (part: Partial<CoreHaldorExportConfiguration>) => void
    active: boolean
    onValidChange: (arg: boolean) => void 
}

const HaldorConfiguration = (props: HaldorConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [open, setOpen] = useState<boolean>(true);

    const validation = validator(props.configuration, props.active);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration, props.active]);

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.HaldorExport.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>

                        <HaldorClassSetting
                            title={t('haldorExport.classSettings.title', {ns: 'configuration'})}
                            name='classSettings'
                            value={props.configuration.classSettings}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                        />

                        <HaldorEducationGroupSetting
                            title={t('haldorExport.educationGroupSettings.title', { ns: 'configuration' })}
                            name='educationGroupSettings'
                            value={props.configuration.educationGroupSettings}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                        />

                        <HaldorMentorGroupSetting
                            title={t('haldorExport.mentorGroupSettings.title', { ns: 'configuration' })}
                            name='mentorGroupSettings'
                            value={props.configuration.mentorGroupSettings}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                        />

                        <BoolInput
                            title={t('haldorExport.includeCivicNo', { ns: 'configuration' })}
                            name='includeCivicNo'
                            value={props.configuration.includeCivicNo}
                            updateValue={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                        />

                        <BoolInput
                            title={t('haldorExport.includeCustodians', { ns: 'configuration' })}
                            name='includeCustodians'
                            value={props.configuration.includeCustodians}
                            updateValue={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                        />

                        <h5>{t('haldorExport.createTeacherValidationFileTitle', { ns: 'configuration' })}</h5>
                        <BoolInput
                            titleSmall={true}
                            title={t('haldorExport.createTeacherValidationFileLabel', { ns: 'configuration' })}
                            name='createTeacherValidationFile'
                            value={props.configuration.createTeacherValidationFile}
                            updateValue={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                        />

                        <FormGroup>
                            <h5>{t('haldorExport.access.title', { ns: 'configuration' })}</h5>
                            <FormGroup>
                                <Label for="blobContainerUrl">{t('haldorExport.access.blobContainerUrl.title', { ns: 'configuration' })}</Label>
                                <Input
                                    type="text"
                                    name="blobContainerUrl"
                                    id="blobContainerUrl"
                                    placeholder={t('haldorExport.access.blobContainerUrl.title', { ns: 'configuration' })}
                                    disabled={props.disabled}
                                    value={props.configuration.blobContainerUrl || ''}
                                    onChange={(e) => props.updatePart({ blobContainerUrl: e.target.value })}
                                    invalid={validation.indexOf('blobContainerUrl') !== -1}
                                    autoComplete="off"
                                />
                                <FormFeedback>{t('haldorExport.access.blobContainerUrl.invalidFeedback', { ns: 'configuration' })}</FormFeedback>
                            </FormGroup>
                            <FormGroup>
                                <Label for="blobSourceKey">{t('haldorExport.access.blobSourceKey.title', { ns: 'configuration' })}</Label>
                                <Input
                                    type="text"
                                    name="blobSourceKey"
                                    id="blobSourceKey"
                                    placeholder={t('haldorExport.access.blobSourceKey.title', { ns: 'configuration' })}
                                    value={props.configuration.blobSourceKey || ''}
                                    disabled={props.disabled}
                                    onChange={(e) => props.updatePart({ blobSourceKey: e.target.value })}
                                    invalid={validation.indexOf('blobSourceKey') !== -1}
                                    autoComplete="off"
                                />
                                <FormFeedback>{t('haldorExport.access.blobSourceKey.invalidFeedback', { ns: 'configuration' })}</FormFeedback>
                            </FormGroup>
                        </FormGroup>

                    </div>
                </CardBody>
            </Collapse>
        </Card>)
}

export default HaldorConfiguration;
